import { FunctionComponent, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Tag from "../components/Tag";
import AppTag from "./AppTag";
import Row from "../components/Row";
import styled from "styled-components";
import { useConnectionsHandlers } from "../services/actions/connections/useConnectionsHandlers";

interface IConnectionTagsProps {
    tags: any
    tagsCalculate?: boolean
    backgroundColor?: string;
    maxWidth?: number;
    connection?: any;
}

const ConnectionTags: FunctionComponent<IConnectionTagsProps> = ({ tags, maxWidth = 0, backgroundColor, connection }) => {
    const [visibleCount, setVisibleCount] = useState(0);
    const [localTags, setLocalTags] = useState<any[]>(tags || []);
    const pendingRemovedIdsRef = useRef<Set<string>>(new Set());
    const [queue, setQueue] = useState<any[]>([]);
    const [processing, setProcessing] = useState(false);
    const tagRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [showMoreDropdown, setShowMoreDropdown] = useState(false);
    const moreDropdownRef = useRef<HTMLDivElement>(null);
    const moreTriggerRef = useRef<HTMLDivElement>(null);
    const [moreDropdownPosition, setMoreDropdownPosition] = useState({ x: 0, y: 0, positioned: false });
    const { applyRemoveTagsToConnection } = useConnectionsHandlers();

    // Compute how many tags fit within maxWidth, reserving exact space for "+N more" when needed
    const moreMeasureRef = useRef<HTMLDivElement>(null);
    
    // keep local tags in sync with props, but filter out pending removed to avoid flicker
    useEffect(() => {
        const pending = pendingRemovedIdsRef.current;
        const next = (tags || []).filter((t: any) => !pending.has(String(t?._id)));
        setLocalTags(next);
    }, [tags]);
    useEffect(() => {
        if (!localTags?.length) { setVisibleCount(0); return; }
        if (!maxWidth || maxWidth <= 0) { setVisibleCount(localTags.length); return; }

        const GAP_SIZE = 5;
        const n = localTags.length;
        const widths: number[] = localTags.map((_: any, i: number) => tagRefs.current[i]?.offsetWidth || 0);
        const sumAll = widths.reduce((s, w) => s + w, 0);
        const gapsAll = Math.max(0, n - 1) * GAP_SIZE;
        const baseMoreWidth = moreMeasureRef.current?.offsetWidth || 80;
        const baseMoreTextLen = `${Math.max(1, n - 1)} more`.length;

        // Try to fit all without more chip
        if (sumAll + gapsAll <= maxWidth) {
            setVisibleCount(n);
            return;
        }

        let chosen = 0;
        for (let k = n - 1; k >= 0; k--) {
            const hidden = n - k;
            const tagsWidthK = widths.slice(0, k).reduce((s, w) => s + w, 0);
            const gapsK = Math.max(0, k - 1) * GAP_SIZE;
            let needed = tagsWidthK + gapsK;
            if (hidden > 0) {
                const moreTextLen = `${hidden} more`.length;
                const moreWidth = Math.ceil(baseMoreWidth * (moreTextLen / baseMoreTextLen));
                needed += GAP_SIZE + moreWidth; // gap + more chip
            }
            if (needed <= maxWidth) { chosen = k; break; }
        }
        setVisibleCount(chosen);
    }, [localTags, maxWidth]);

    // Position portal dropdown (avoids table overflow clipping with few rows)
    useEffect(() => {
        if (!showMoreDropdown || !moreTriggerRef.current) return;

        const updatePosition = () => {
            const trigger = moreTriggerRef.current;
            if (!trigger) return;

            const rect = trigger.getBoundingClientRect();
            const gap = 4;
            const menuHeight = moreDropdownRef.current?.offsetHeight || 120;
            const viewportHeight = window.innerHeight;
            let y = rect.bottom + gap;

            if (viewportHeight - rect.bottom < menuHeight + gap && rect.top > menuHeight + gap) {
                y = rect.top - menuHeight - gap;
            }

            setMoreDropdownPosition({ x: rect.left, y, positioned: true });
        };

        const rafId = requestAnimationFrame(() => {
            updatePosition();
            requestAnimationFrame(updatePosition);
        });
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [showMoreDropdown, visibleCount, localTags.length]);

    const hiddenCount = Math.max(0, (localTags?.length || 0) - visibleCount);

    // close more dropdown on outside click
    useEffect(() => {
        if (!showMoreDropdown) return;
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            if (moreDropdownRef.current?.contains(target)) return;
            if (moreTriggerRef.current?.contains(target)) return;
            setShowMoreDropdown(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMoreDropdown]);

    const handleRemoveTag = async (tag: any) => {
        if (!connection?._id) return;
        const tagId = String(tag?._id);
        // if already pending, ignore duplicate
        if (pendingRemovedIdsRef.current.has(tagId)) return;
        // optimistic remove and mark pending
        pendingRemovedIdsRef.current.add(tagId);
        setLocalTags(prev => prev.filter((t: any) => String(t?._id) !== tagId));
        // enqueue API call to process sequentially
        setQueue(q => [...q, { type: 'remove', tagId, tag }]);
    };

    // process queue sequentially
    useEffect(() => {
        const run = async () => {
            if (processing || queue.length === 0) return;
            setProcessing(true);
            const action = queue[0];
            try {
                if (action.type === 'remove') {
                    await applyRemoveTagsToConnection(connection._id, [], [action.tagId]);
                }
            } catch (e) {
                // revert on failure and clear pending
                pendingRemovedIdsRef.current.delete(action.tagId);
                setLocalTags(prev => {
                    const exists = prev.some((t: any) => String(t?._id) === action.tagId);
                    return exists ? prev : [...prev, action.tag];
                });
            }
            setQueue(q => q.slice(1));
            setProcessing(false);
        };
        run();
    }, [processing, queue, applyRemoveTagsToConnection, connection?._id]);

    return (
        <TagRow style={{ maxWidth: `${maxWidth}px` }} $hoverAdd>
            {/* Hidden tags for measurement */}
            <div style={{
                position: 'absolute',
                visibility: 'hidden',
                height: 0,
                pointerEvents: 'none',
                display: 'block',
                width: 'auto',
                left: 0, top: 0
            }}>
                {localTags?.map((tag: any, idx: number) => (
                    <div key={tag?._id} ref={el => tagRefs.current[idx] = el} style={{ display: 'inline-block', width: 'auto' }}>
                        <AppTag
                            tag={tag}
                            onDestructiveClickHandler={connection?._id ? () => {} : undefined}
                        />
                    </div>
                ))}
                {/* measure more chip */}
                <div ref={moreMeasureRef} style={{ display: 'inline-block', width: 'auto' }}>
                    <Tag className="connection-tags-class" title={`${Math.max(1, (localTags?.length || 1) - 1)} more`} />
                </div>
            </div>
            {/* Visible tags */}
            {localTags?.slice(0, visibleCount).map((tag: any) => (
                <div key={tag?._id} onClick={(e) => e.stopPropagation()}>
                    <AppTag
                        tag={tag}
                        onDestructiveClickHandler={connection?._id ? () => handleRemoveTag(tag) : undefined}
                    />
                </div>
            ))}
            {hiddenCount > 0 && (
                <MoreTagWrapper>
                    <div
                        ref={moreTriggerRef}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowMoreDropdown((open) => {
                                if (!open) setMoreDropdownPosition({ x: 0, y: 0, positioned: false });
                                return !open;
                            });
                        }}
                    >
                        <Tag
                            className="connection-tags-class"
                            title={`${hiddenCount} more`}
                        />
                    </div>
                    {showMoreDropdown && createPortal(
                        <PortalMoreDropdown
                            ref={moreDropdownRef}
                            style={{
                                position: 'fixed',
                                top: `${moreDropdownPosition.y}px`,
                                left: `${moreDropdownPosition.x}px`,
                                zIndex: 10050,
                                visibility: moreDropdownPosition.positioned ? 'visible' : 'hidden',
                                pointerEvents: moreDropdownPosition.positioned ? 'auto' : 'none',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreDropdownList>
                                {localTags?.slice(visibleCount).map((tag: any) => (
                                    <MoreDropdownItem key={tag?._id} onClick={(e) => e.stopPropagation()}>
                                        <AppTag
                                            tag={tag}
                                            onDestructiveClickHandler={connection?._id ? () => handleRemoveTag(tag) : undefined}
                                        />
                                    </MoreDropdownItem>
                                ))}
                            </MoreDropdownList>
                        </PortalMoreDropdown>,
                        document.body
                    )}
                </MoreTagWrapper>
            )}
        </TagRow>
    );
};

const TagRow = styled(Row)<{ $hoverAdd?: boolean }>`
    position: relative;
    align-items: center;
    gap: 5px;
    overflow: visible;
    ${({ $hoverAdd }) => $hoverAdd && `
        .add-tag-button { opacity: 0; pointer-events: none; transition: opacity .15s ease-in-out; }
        &:hover .add-tag-button { opacity: 1; pointer-events: auto; }
    `}
`;

const MoreTagWrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const PortalMoreDropdown = styled.div`
    background: white;
    border: 1px solid #eee;
    border-radius: 4px;
    min-width: 160px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    padding: 8px;
    box-sizing: border-box;
`;

const MoreDropdownList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MoreDropdownItem = styled.div`
    display: flex;
    align-items: center;
`;

export default ConnectionTags